import { useEffect, useMemo, useRef, useState } from "react";
import { AccessBadge } from "@/components/app/access-badge";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Search,
  Calculator,
  RefreshCw,
  FlaskConical,
  Atom,
  Sigma,
  Dna,
  BrainCircuit,
  ArrowUpRight,
  ChevronRight,
  Sparkles,
  Command,
  type LucideIcon,
} from "lucide-react";
import { TOOLKIT_CATEGORIES, TOOLKIT_TOOLS, searchTools } from "@/lib/toolkit/registry";
import { ThemeToggle } from "@/components/app/theme-toggle";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/toolkit/")({
  head: () => ({
    ...seo({
      title: "JEE Toolkit — Calculators, Formulae & Reference",
      description:
        "Scientific calculators, unit converters, the periodic table, name reactions and formula sheets for JEE and NEET, in one fast toolkit.",
      path: "/toolkit",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "JEE Toolkit", path: "/toolkit" },
      ]),
    ],
  }),
  component: ToolkitHome,
});

const QUICK_FILTERS = [
  { label: "All Tools", value: "" },
  { label: "Volume", value: "convert-volume" },
  { label: "Percentage Calculator", value: "percentage-calculator" },
  { label: "Energy", value: "convert-energy" },
  { label: "Speed & Velocity", value: "convert-speed" },
  { label: "Logarithms", value: "algebra" },
  { label: "Stoichiometry", value: "mole-gram" },
];

const TOOL_ACCENT =
  "border-lime-600/25 text-lime-700 bg-lime-500/10 group-hover:border-lime-600/50 dark:border-lime-400/25 dark:text-lime-400 dark:bg-lime-400/10 dark:group-hover:border-lime-400/50";

const CATEGORY_META: Record<
  string,
  { icon: LucideIcon; accent: string; title: string; description: string }
> = {
  calculators: {
    icon: Calculator,
    title: "Calculators",
    description: "Scientific, log, antilog & complex root calculators.",
    accent: TOOL_ACCENT,
  },
  converters: {
    icon: RefreshCw,
    title: "Unit Conversions",
    description: "SI to CGS, pressure units, energy & thermal constants.",
    accent: TOOL_ACCENT,
  },
  chemistry: {
    icon: FlaskConical,
    title: "Chemistry Lab",
    description: "Molarity, normality, periodic trends & reaction balancers.",
    accent: TOOL_ACCENT,
  },
  physics: {
    icon: Atom,
    title: "Physics Engines",
    description: "Kinematics formulas, circuits & optics lens equation solvers.",
    accent: TOOL_ACCENT,
  },
  maths: {
    icon: Sigma,
    title: "Mathematics",
    description: "Matrix determinants, conic sections & integral reference sheets.",
    accent: TOOL_ACCENT,
  },
  biology: {
    icon: Dna,
    title: "Biology Reference",
    description: "Genetics ratios, taxonomy trees & human physiology constants.",
    accent: TOOL_ACCENT,
  },
  memory: {
    icon: BrainCircuit,
    title: "Memory & Active Recall",
    description: "Spaced repetition revision cards, formula sheets & quick mnemonics.",
    accent: TOOL_ACCENT,
  },
};

function ToolkitHome() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All Tools");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const categories = useMemo(() => {
    return TOOLKIT_CATEGORIES.map((cat) => {
      const meta = CATEGORY_META[cat.id] ?? {
        icon: Sparkles,
        title: cat.name,
        description: cat.description,
        accent: TOOL_ACCENT,
      };
      const count = TOOLKIT_TOOLS.filter((t) => t.category === cat.id).length;
      return { ...cat, ...meta, count };
    });
  }, []);

  const filteredCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (cat) =>
        cat.title.toLowerCase().includes(q) ||
        cat.description.toLowerCase().includes(q) ||
        cat.name.toLowerCase().includes(q),
    );
  }, [categories, searchQuery]);

  const toolHits = useMemo(() => {
    const q = searchQuery.trim();
    return q ? searchTools(q) : [];
  }, [searchQuery]);

  const searching = searchQuery.trim().length >= 1;

  return (
    <div className="relative w-full min-h-screen overflow-x-clip bg-[var(--background)] text-[var(--foreground)] dark:bg-[var(--background)] dark:text-[var(--foreground)] p-4 sm:p-8 transition-colors duration-300">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-32 h-72 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(111,174,53,0.10),transparent_70%)] dark:bg-[radial-gradient(60%_60%_at_50%_0%,rgba(111,174,53,0.16),transparent_70%)]"
      />
      <div className="relative max-w-4xl mx-auto space-y-6">
        {/* Crisp Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-b border-[rgba(90,110,85,0.18)] dark:border-[rgba(130,150,125,0.16)] pb-5">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-lime-600 dark:bg-lime-400 shadow-[0_0_10px_rgba(130,195,65,0.7)] animate-pulse" />
              <p className="text-[11px] font-mono font-semibold uppercase tracking-widest text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
                Core Utilities Engine
              </p>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--foreground)] dark:text-[var(--foreground)] mt-1">
              JEE + NEET Toolkit <AccessBadge level="FREE" />
            </h1>
            <p className="text-xs text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)] mt-1">
              High-precision calculators, constant reference tables & revision algorithms.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="px-2.5 py-1 text-[11px] font-mono font-semibold rounded-md bg-lime-500/10 border border-lime-600/30 text-lime-700 dark:bg-lime-400/10 dark:border-lime-400/25 dark:text-lime-300">
              {TOOLKIT_TOOLS.length} Tools Active
            </span>
            <ShareButton />
            <ThemeToggle className="rounded-xl border border-[rgba(90,110,85,0.22)] bg-white text-[var(--foreground)] dark:border-[rgba(130,150,125,0.25)] dark:bg-[var(--background)] dark:text-[var(--foreground)]" />
          </div>

        </div>

        {/* Professional Search Bar with Shortcut Badge */}
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
            <Search className="w-4 h-4" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setActiveFilter("");
            }}
            placeholder="Search tools, formulas, constants, reactions... (e.g., 'molarity', 'kinetic energy')"
            className="w-full pl-10 pr-20 py-3 rounded-2xl bg-white border border-[rgba(90,110,85,0.22)] text-xs sm:text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] shadow-sm dark:bg-[var(--background)]/90 dark:border-[rgba(130,150,125,0.25)] dark:text-[var(--foreground)] dark:placeholder-[var(--muted-foreground)] dark:shadow-none focus:outline-none focus:border-lime-500/60 dark:focus:border-lime-400/50 focus:shadow-[0_0_0_4px_rgba(130,195,65,0.10)] transition"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-mono text-[var(--muted-foreground)] bg-[var(--surface-elevated)] border border-[rgba(90,110,85,0.22)] dark:text-[var(--muted-foreground)] dark:bg-[var(--surface)] dark:border-[rgba(130,150,125,0.25)] rounded-md">
              <Command className="w-3 h-3" /> K
            </kbd>
          </div>
        </div>

        {/* Quick Filter Pill Chips */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scroll no-scrollbar">
          {QUICK_FILTERS.map((filter) => {
            const active = activeFilter === filter.label;
            return (
              <button
                key={filter.label}
                onClick={() => {
                  setActiveFilter(filter.label);
                  if (filter.value) {
                    const tool = TOOLKIT_TOOLS.find((t) => t.id === filter.value);
                    setSearchQuery(tool ? tool.name : filter.label);
                  } else {
                    setSearchQuery("");
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-all ${
                  active
                    ? "bg-lime-500/15 text-primary border-lime-600/45 dark:bg-lime-400/15 dark:text-[var(--foreground)] dark:border-lime-400/50 shadow-[0_0_16px_-4px_rgba(130,195,65,0.5)]"
                    : "bg-white border-[rgba(90,110,85,0.18)] text-[var(--muted-foreground)] hover:border-lime-600/40 hover:text-[var(--foreground)] dark:bg-[var(--background)]/70 dark:border-[rgba(130,150,125,0.16)] dark:text-[var(--muted-foreground)] dark:hover:border-lime-400/30 dark:hover:text-[var(--foreground)]"
                }`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>

        {/* Search results */}
        {searching ? (
          <section className="space-y-4 pb-8">
            {toolHits.length === 0 && filteredCategories.length === 0 ? (
              <p className="rounded-3xl border border-dashed border-[rgba(90,110,85,0.28)] dark:border-[rgba(130,150,125,0.25)] px-6 py-10 text-center text-sm text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
                Nothing found for “{searchQuery}”. Try a formula name, an element or a unit.
              </p>
            ) : null}

            {toolHits.length > 0 ? (
              <>
                <h2 className="flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
                  <Search className="h-3.5 w-3.5" /> Tools
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {toolHits.map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <Link
                        key={tool.id}
                        to="/toolkit/$toolId"
                        params={{ toolId: tool.id }}
                        className="group relative p-5 rounded-3xl bg-white border border-[rgba(90,110,85,0.16)] shadow-sm dark:bg-[var(--background)]/80 dark:border-[rgba(130,150,125,0.16)] dark:shadow-none hover:border-lime-500/50 dark:hover:border-lime-400/40 hover:shadow-[0_0_28px_-12px_rgba(130,195,65,0.6)] transition-all duration-200 flex flex-col justify-between"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="p-2.5 rounded-2xl border border-lime-600/25 text-lime-700 bg-lime-500/10 dark:border-lime-400/25 dark:text-lime-400 dark:bg-lime-400/10 transition-transform group-hover:scale-105 duration-200">
                            <Icon className="w-5 h-5" />
                          </div>
                          <ArrowUpRight className="w-3.5 h-3.5 text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)] group-hover:text-lime-600 dark:group-hover:text-lime-300 transition" />
                        </div>
                        <h3 className="text-sm font-bold text-[var(--foreground)] dark:text-[var(--foreground)] tracking-tight">
                          {tool.name}
                        </h3>
                        <p className="text-xs text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)] mt-1 line-clamp-2 leading-relaxed">
                          {tool.description}
                        </p>
                      </Link>
                    );
                  })}
                </div>
              </>
            ) : null}
          </section>
        ) : null}

        {/* Category menu — compact horizontal rows */}
        {!searching && (
          <div className="space-y-2.5">
            {filteredCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={cat.id}
                  to="/toolkit/c/$categoryId"
                  params={{ categoryId: cat.id }}
                  className="group relative flex items-center gap-4 rounded-[28px] border border-border/70 bg-card px-4 py-3.5 shadow-[var(--shadow)] transition-all duration-200 hover:-translate-y-px hover:border-primary/40 sm:px-5 sm:py-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/25 bg-primary/10 text-primary transition-transform duration-200 group-hover:scale-105">
                    <Icon className="h-5 w-5" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold tracking-tight text-card-foreground">
                      {cat.title}
                    </span>
                    <span className="mt-0.5 block line-clamp-2 text-xs leading-relaxed text-muted-foreground sm:line-clamp-1">
                      {cat.description}
                    </span>
                  </span>

                  <span className="hidden shrink-0 font-mono text-[10px] text-muted-foreground sm:inline">
                    {cat.count} tools
                  </span>

                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              );
            })}
          </div>
        )}



        {/* Footer Minimal Status */}
        <div className="p-4 rounded-3xl border border-[rgba(90,110,85,0.16)] bg-white shadow-sm dark:border-[rgba(130,150,125,0.16)] dark:bg-[var(--background)]/70 dark:shadow-none flex items-center justify-between gap-3 text-[11px] text-[var(--muted-foreground)] dark:text-[var(--muted-foreground)]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-lime-600 dark:text-lime-400" />
            <span>Need a custom formula or unit converter?</span>
          </div>
          <a
            href="https://t.me/thequantummindsofficialbot"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 font-semibold text-lime-700 dark:text-lime-300 hover:underline"
          >
            Request module →
          </a>
        </div>
      </div>
    </div>
  );
}
